import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { com, kotlin, Nullable } from '@eidu/form'
import { Alert, Button, Stack, Typography } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { AuthContext, IAuthContext } from 'react-oauth2-code-pkce'
import { EntityTypesContext } from '../../io/context/EntityTypes'
import DraftFieldValue, {
  draftFieldValuesToFieldValues,
  fieldValueToDraftFieldValue,
  fieldValueToString,
} from '../../domain/entity/DraftFieldValue'
import EqualityHashMap from '../../util/EqualityHashMap'
import { requireNotUndefinedOrNull } from '../../util/require'
import createEntityToCreate from './createEntityToCreate'
import hasFieldError from '../../domain/entity/hasFieldError'
import { logException } from '../../util/Logging'
import useEntityRepository from '../../io/useEntityRepository'
import { createAuthContext } from '../../api/authorization/AuthenticationContext'
import usePermittedActions from '../../io/usePermittedActions'
import { isNotUndefinedOrNull } from '../../util/predicates'
import getFormContentText from '../../api/form/getFormContentText'
import postFormContentText from '../../api/form/postFormContentText'
import PermissionError from './PermissionError'
import { ConflictError } from '../../api/entity/data/postEntities'
import { useSnackBar } from '../../components/feedback/SnackBarProvider'
import EntityFormInput from '../../components/input/EntityFormInput'
import LoginIdentifierField from '../../components/input/LoginIdentifierField'
import FormContentInput from '../../components/input/FormContentInput'
import ConfirmationDialog from '../../components/feedback/ConfirmationDialog'
import LoadingOverlay from '../../components/feedback/LoadingOverlay'
import FieldValue = com.eidu.sharedlib.entity.field.FieldValue
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import FieldId = com.eidu.sharedlib.entity.field.FieldId
import Entity = com.eidu.sharedlib.entity.Entity
import entityIdFromString = com.eidu.sharedlib.entity.entityIdFromString
import entityTypeIdFromString = com.eidu.sharedlib.entity.type.entityTypeIdFromString
import EntityId = com.eidu.sharedlib.entity.EntityId
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeKind = com.eidu.sharedlib.entity.type.EntityTypeKind
import GlobalAction = com.eidu.sharedlib.entity.permission.GlobalAction
import PermittedGlobalAction = com.eidu.sharedlib.entity.permission.PermittedGlobalAction
import formContentIdFromString = com.eidu.sharedlib.entity.kind.form.formContentIdFromString
import getFormContentId = com.eidu.sharedlib.entity.getFormContentId
import formContentIdKey = com.eidu.sharedlib.entity.formContentIdKey
import EntityAction = com.eidu.sharedlib.entity.permission.EntityAction
import PermittedEntityAction = com.eidu.sharedlib.entity.permission.PermittedEntityAction
import Page = com.eidu.sharedlib.form.definition.Page
import KtList = kotlin.collections.KtList
import emailLoginIdentifierKey = com.eidu.sharedlib.entity.emailLoginIdentifierKey
import phoneNumberLoginIdentifierKey = com.eidu.sharedlib.entity.phoneNumberLoginIdentifierKey
import PhoneNumber = com.eidu.sharedlib.user.PhoneNumber
import isRealMobilePhoneNumber = com.eidu.sharedlib.user.isRealMobilePhoneNumber
import ExternalValueKey = com.eidu.sharedlib.entity.user.ExternalValueKey
import linkKeyFromString = com.eidu.sharedlib.entity.link.linkKeyFromString
import Link from '../../domain/entity/Link'
import LearningUnitEditorWrapper from '../../ui/microfrontend/LearningUnitEditorWrapper'
import { useFormErrors } from '../../util/FormErrorBus'

// Validation utility functions
const isValidEmailOrEmpty = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return email === '' || emailRegex.test(email)
}

const isValidPhoneNumberOrEmpty = (phone: string): boolean => {
  const phoneNumber = PhoneNumber.fromString(phone)
  return phone === '' || !!(phoneNumber && isRealMobilePhoneNumber(phoneNumber))
}

const getNonNullFieldsFromEntity = (entity: Entity): ReadonlyMap<FieldId, DraftFieldValue> =>
  new EqualityHashMap<FieldId, DraftFieldValue>(
    Array.from(entity.valuesByFieldId.asJsReadonlyMapView())
      .filter((it): it is [FieldId, FieldValue] => it[1] !== null)
      .map(([id, value]) => [id, fieldValueToDraftFieldValue(id, value)])
  )

export type ReadyCreateOrEditPageProps = {
  entityId?: EntityId
  entityType: EntityType
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>
}

const ReadyCreateOrEditPage = ({ entityId, entityType, entityTypes }: ReadyCreateOrEditPageProps) => {
  const entityRepository = useEntityRepository(
    {
      typeId: entityType.id,
      types: entityTypes,
    },
    [entityType.id.toString(), entityTypes.size]
  )

  const authContext = createAuthContext(useContext<IAuthContext>(AuthContext))

  const requiredEntityActions: EntityAction[] = [EntityAction.ReadEntity, EntityAction.UpdateEntity]

  const requested = useRef([
    new PermittedGlobalAction(GlobalAction.ManageAuthentication),
    ...(entityId ? requiredEntityActions.map((entityAction) => new PermittedEntityAction(entityAction, entityId)) : []),
  ])
  const [permissions] = usePermittedActions(requested.current)

  const missingEntityActions = entityId
    ? requiredEntityActions.filter(
        (action) =>
          !permissions?.some(
            (permittedAction) =>
              permittedAction instanceof PermittedEntityAction &&
              permittedAction.target.equals(entityId) &&
              permittedAction.action === action
          )
      )
    : []

  const hasRequiredEntityActions = missingEntityActions.length === 0

  // Important: entityId must not change between undefined and
  // not undefined, otherwise React's hook order will be messed up.
  const entity = entityId !== undefined ? entityRepository.useItem(entityId) : undefined

  const navigate = useNavigate()
  const { showSnackBar } = useSnackBar()

  const [formContentLoading, setFormContentLoading] = useState(false)

  // Important: all use* need to run, so don't replace this by anything using short-circuiting!
  const error = [entityRepository.useError(), entity === null ? 'Entity not found' : undefined]
    .find((it) => !!it)
    ?.let((it) => String(it))
  const loading =
    [entityRepository.useLoading(), entityId !== undefined && entity === undefined].some((it) => it) ||
    formContentLoading

  const [submitting, setSubmitting] = useState(false)

  const [fields, setFields] = useState<ReadonlyMap<FieldId, DraftFieldValue>>(new Map())
  const updateField = (value: DraftFieldValue) => setFields((it) => new EqualityHashMap(it).set(value.fieldId, value))

  const [formContent, setFormContent] = useState<KtList<Page>>(KtList.fromJsArray([]))

  const [formContentErrorMessage, setFormContentErrorMessage] = useState<string>()

  const [phone, setPhone] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [phoneError, setPhoneError] = useState<string | undefined>()
  const [emailError, setEmailError] = useState<string | undefined>()
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)

  useEffect(() => {
    if (entity) {
      setFields(getNonNullFieldsFromEntity(entity.entity))

      const externalValues = entity.entity.externalValues?.asJsReadonlyMapView() ?? new Map()
      setPhone(externalValues.get(phoneNumberLoginIdentifierKey) || '')
      setEmail(externalValues.get(emailLoginIdentifierKey) || '')

      const formContentId = getFormContentId(entity.entity) ?? undefined
      if (isNotUndefinedOrNull(formContentId)) {
        setFormContentLoading(true)
        getFormContentText({
          formContentId: formContentId.toString(),
          authContext: requireNotUndefinedOrNull(authContext),
        }).then((content) => {
          setFormContentLoading(false)

          const formContentWithPageKeys = KtList.fromJsArray(
            content
              .asJsReadonlyArrayView()
              .map((page: Page, index: number) =>
                !page.key ? page.copy(page.title, page.items, `page_${Date.now()}_${index}`) : page
              )
          )
          setFormContent(formContentWithPageKeys)
          setFormContentLoading(false)
        })
      } else {
        setFormContent(KtList.fromJsArray([]))
      }
    }
  }, [entity])

  useEffect(() => {
    if (!isValidEmailOrEmpty(email) && hasAttemptedSubmit) {
      setEmailError('Invalid email format')
    } else {
      setEmailError(undefined)
    }

    if (!isValidPhoneNumberOrEmpty(phone) && hasAttemptedSubmit) {
      setPhoneError('Invalid phone number format')
    } else {
      setPhoneError(undefined)
    }
  }, [email, phone, hasAttemptedSubmit])

  const { hasErrors: hasFormErrors } = useFormErrors()

  const valid = useMemo(() => {
    if (!entityType) return false

    const fieldsValid = entityType.fields.asJsReadonlyArrayView().every((field) => {
      const fieldValue = fields.get(field.id) ?? undefined
      return !hasFieldError(field, fieldValue, new Map())
    })

    const loginIdentifiersValid = !hasAttemptedSubmit || (!phoneError && !emailError)

    return fieldsValid && loginIdentifiersValid && !hasFormErrors
  }, [entityType, fields, hasAttemptedSubmit, phoneError, emailError, hasFormErrors])

  const createFormContent = (content: KtList<Page> | undefined) =>
    content
      ? postFormContentText({
          pages: content,
          authContext: requireNotUndefinedOrNull(authContext),
        }).then(
          (contentId) => formContentIdFromString(contentId),
          (err) => {
            setFormContentErrorMessage(err.message)
            throw Error()
          }
        )
      : Promise.reject()

  const customEditorSubmitRef = useRef<() => Promise<Link[]>>()

  const submit = useCallback(async () => {
    setHasAttemptedSubmit(true)

    let hasEmailError = false
    let hasPhoneError = false

    if (!isValidEmailOrEmpty(email)) {
      setEmailError('Invalid email format')
      hasEmailError = true
    }

    if (!isValidPhoneNumberOrEmpty(phone)) {
      setPhoneError('Invalid phone number format')
      hasPhoneError = true
    }

    if (hasEmailError || hasPhoneError) return
    setSubmitting(true)

    const updatedLinks: Link[] = []
    const customEditorEntityLinks = await customEditorSubmitRef.current?.()
    if (customEditorEntityLinks) {
      updatedLinks.push(...customEditorEntityLinks)
    }
    try {
      let externalValues: Map<ExternalValueKey, Nullable<string>>

      const phoneNumber = PhoneNumber.fromString(phone)

      if (!entity) {
        externalValues = new Map<ExternalValueKey, Nullable<string>>()
        externalValues.set(phoneNumberLoginIdentifierKey, phoneNumber?.normalized || null)
        externalValues.set(emailLoginIdentifierKey, email || null)
      } else {
        externalValues = new Map<ExternalValueKey, Nullable<string>>()
        externalValues.set(phoneNumberLoginIdentifierKey, phoneNumber?.normalized || null)
        externalValues.set(emailLoginIdentifierKey, email || null)
      }

      if (!entity) {
        const localEntityType = requireNotUndefinedOrNull(entityType)
        try {
          const fieldValues = [...Array.from(fields.values())]
          const newContentId =
            entityType.kind === EntityTypeKind.Form && formContent ? await createFormContent(formContent) : undefined
          const formLinks = newContentId ? [{ key: formContentIdKey, value: newContentId.toString() }] : []
          updatedLinks.push(...formLinks)
          await entityRepository.createEntity(
            createEntityToCreate(entityType.id, fieldValues, updatedLinks, externalValues || new Map())
          )
          showSnackBar(`Created ${localEntityType.name}`, 'success')
          navigate(-1)
        } catch (e) {
          logException(e)
          if (e instanceof ConflictError) {
            showSnackBar(`Entity login identifier already exists. Please check email and phone number.`, 'error')
          } else {
            showSnackBar(`Failed to create ${localEntityType.name}`, 'error')
          }
        }
      } else {
        const localEntityType = requireNotUndefinedOrNull(entityType)
        const fieldValues = [...Array.from(fields.values())].filter((field) => {
          const originalValue = entity.entity.valuesByFieldId.asJsReadonlyMapView().get(field.fieldId)
          return !originalValue || fieldValueToString(originalValue) !== field.value
        })
        const newFormContentId =
          entityType.kind === EntityTypeKind.Form && formContent ? await createFormContent(formContent) : null
        const formLinks = newFormContentId ? [{ key: formContentIdKey, value: newFormContentId.toString() }] : []
        updatedLinks.push(...formLinks)
        try {
          await entityRepository.modifyEntity(entity.entity.id, {
            valuesToUpdateByFieldId: draftFieldValuesToFieldValues(fieldValues),
            linksToUpdate: new Map(updatedLinks.map((link) => [link.key, link.value])),
            externalValuesToUpdate: externalValues,
          })
          showSnackBar(`${localEntityType.name} updated`, 'success')
          navigate(-1)
        } catch (e) {
          logException(e)
          if (e instanceof ConflictError) {
            showSnackBar(`Entity login identifier already exists. Please check email and phone number.`, 'error')
          } else {
            showSnackBar(`Failed to update ${localEntityType.name}`, 'error')
          }
        }
      }
    } finally {
      setSubmitting(false)
    }
  }, [
    entityRepository,
    navigate,
    entityType,
    entity,
    fields,
    formContent,
    showSnackBar,
    phone,
    email,
    phoneNumberLoginIdentifierKey,
    emailLoginIdentifierKey,
    setEmailError,
    setPhoneError,
  ])

  const confirmDelete = async () => {
    const localEntityType = requireNotUndefinedOrNull(entityType)
    try {
      await entityRepository.deleteEntity(requireNotUndefinedOrNull(entityId))
      showSnackBar(`Deleted ${localEntityType.name}`, 'success')
      navigate(-1)
    } catch (e) {
      logException(e)
      showSnackBar(`Failed to delete ${localEntityType.name}`, 'error')
    }
  }

  const loadingOverlayOpen = submitting || loading || !(error || entityType)

  return (
    <Stack padding={3} spacing={3}>
      {entityType && hasRequiredEntityActions && (
        <>
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h4">{entityType.name}</Typography>
          </Stack>
          <Stack padding={0} spacing={3}>
            {entityType.fields.asJsReadonlyArrayView().map((field) => (
              <EntityFormInput
                key={`entity-form-field-${field.id.toString()}`}
                field={field}
                fieldValue={fields.get(field.id)}
                entityTypes={entityTypes}
                updateField={updateField}
              />
            ))}
          </Stack>
          {entityType.kind === EntityTypeKind.User && (
            <LoginIdentifierField
              phone={phone}
              setPhone={setPhone}
              email={email}
              setEmail={setEmail}
              phoneError={phoneError}
              emailError={emailError}
            />
          )}
          {entityType.kind === EntityTypeKind.Form &&
            ((formContent?.asJsReadonlyArrayView().every((page) => page.key) && (
              <FormContentInput
                pages={
                  formContent ||
                  KtList.fromJsArray([new Page('Untitled page', KtList.fromJsArray([]), `page_${Date.now()}`, null)])
                }
                onChange={setFormContent}
                errorMessage={formContentErrorMessage}
              />
            )) ||
              (!loading && (
                <Alert severity={'error'}>
                  Some pages on this form do not have a key set. This is not supported. Contact EIDU if the issue
                  persists.
                </Alert>
              )))}
          {entityType.kind === EntityTypeKind.LearningUnit && entity && (
            <LearningUnitEditorWrapper
              entityId={entity.entity.id.toString()}
              entityLinks={[...(entity.entity.links.asJsReadonlyMapView().entries() || [])].map(([key, value]) => ({
                key,
                value,
              }))}
              linkKeyFromString={linkKeyFromString}
              onRegisterPartialSubmit={(fn: () => Promise<Link[]>) => {
                customEditorSubmitRef.current = fn
              }}
              mode="edit"
            />
          )}
          <Stack direction="row" spacing={3} sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate(-1)}>Cancel</Button>
            {entity && (
              <ConfirmationDialog
                title={`There may still be references to this ${entityType?.name ?? 'entity'} in the system. Are you sure you want to
                      delete this ${entityType?.name ?? 'entity'}? This action cannot be undone.`}
                content={<Typography>Delete</Typography>}
                onConfirm={confirmDelete}
              />
            )}
            <Button variant="contained" onClick={submit} disabled={!valid}>
              Save
            </Button>
          </Stack>
        </>
      )}
      {!hasRequiredEntityActions && !loadingOverlayOpen && (
        <PermissionError missingPermittedActions={missingEntityActions} />
      )}
      <LoadingOverlay isOpen={loadingOverlayOpen} />
    </Stack>
  )
}

const CreateOrEditPage = () => {
  const params = useParams()
  const entityId = params.entityId?.let(entityIdFromString)
  const entityTypeId = entityTypeIdFromString(requireNotUndefinedOrNull(params.entityTypeId))

  const entityTypeRepository = useContext(EntityTypesContext)
  const entityTypes = entityTypeRepository.useAll()
  const entityType = entityTypeRepository.useItem(entityTypeId)
  const error = entityTypeRepository.useError()?.let((it) => String(it))

  return (
    <>
      {entityType && entityTypes && (
        <ReadyCreateOrEditPage entityId={entityId} entityType={entityType} entityTypes={entityTypes} />
      )}
      <LoadingOverlay isOpen={!(error || entityType)} />
    </>
  )
}

export default CreateOrEditPage
